import React from 'react';
import {
  useCurrentUserContext,
  useGlobalizationContext,
  useTranslation,
  useViewsContext,
  useGetViews,
  useGetView,
  useDeleteView,
  useCreateView,
  useUpdateView,
  useNotificationContext,
  useGetPrivateViewsLimit
} from '@cs-cms/state-management';
import { CpsGridView, CPS_NOTIFICATION_TYPE } from '@cs-cms/types';
import { useCpsDialog } from '../../../common/useCpsDialog';
import { useUserViewsButtonStates } from './useUserViewsButtonStates';
import {
  buildUpdateViewRequest,
  findViewById,
  getDefaultViewIdAfterDelete,
  resolveInitialSelectedViewId,
  transformFieldsForCreation
} from './userViewsUtils';

export const useUserViews = () => {
  const { selectedIndex } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const {
    selectedView,
    selectedViewData,
    onUpdateView,
    editingViewState,
    setSelectedView,
    hasUnsavedChanges,
    setHasUnsavedChanges
  } = useViewsContext();
  const { data: views = [], isLoading } = useGetViews(selectedIndex?.id);
  const [showCreateDialog, setShowCreateDialog] = React.useState<boolean>(false);
  const [showRenameDialog, setShowRenameDialog] = React.useState<boolean>(false);

  const { data: currentViewDetails } = useGetView(selectedView, selectedLocale);
  const { mutate: deleteView } = useDeleteView();
  const { mutate: createView } = useCreateView();
  const { mutate: updateView } = useUpdateView();
  const { showDialog, DialogComponent } = useCpsDialog();
  const { showToast } = useNotificationContext();
  const { data: privateViewsLimit } = useGetPrivateViewsLimit(selectedIndex?.id);

  const buttonStates = useUserViewsButtonStates({
    views,
    selectedView,
    selectedViewData,
    editingViewState,
    hasUnsavedChanges: hasUnsavedChanges ?? false,
    privateViewsLimit
  });

  React.useEffect(() => {
    const nextSelectedViewId = resolveInitialSelectedViewId(views, selectedView);

    if (nextSelectedViewId !== undefined && setSelectedView) {
      setSelectedView(nextSelectedViewId);
    }
  }, [views, selectedView, setSelectedView]);

  const confirmUnsavedViewChange = (newViewId: number): void => {
    showDialog({
      title: translate('custom.settings.views.private.unsavedChangesTitle'),
      message: translate('custom.settings.views.private.unsavedChangesMessage'),
      confirmButtonLabel: translate('custom.common.discard'),
      discardButtonLabel: translate('custom.common.cancel'),
      onConfirm: confirmed => {
        if (confirmed && setSelectedView) {
          setHasUnsavedChanges?.(false);
          setSelectedView(newViewId);
        }
      }
    });
  };

  const handleViewChange = (event: { value: CpsGridView }): void => {
    const newViewId = event.value.id;

    if (hasUnsavedChanges) {
      confirmUnsavedViewChange(newViewId);
      return;
    }

    if (setSelectedView) {
      setSelectedView(newViewId);
    }
  };

  const handleUpdateView = (): void => {
    if (!selectedView || !selectedViewData || !onUpdateView) {
      return;
    }

    const updateViewRequest = buildUpdateViewRequest(selectedView, selectedViewData.name, editingViewState);

    showDialog({
      title: translate('custom.common.saveChangesTitle'),
      message: translate('custom.common.saveChangesMessage'),
      confirmButtonLabel: translate('custom.common.save'),
      discardButtonLabel: translate('custom.common.cancel'),
      onConfirm: confirmed => {
        if (confirmed) {
          onUpdateView(updateViewRequest);
          setHasUnsavedChanges?.(false);
        }
      }
    });
  };

  const handleRenameViewSubmit = (viewId: number, newName: string): void => {
    if (selectedView == null) {
      return;
    }

    updateView(
      {
        viewId: Number(viewId),
        name: newName
      },
      {
        onSuccess: () => {
          setShowRenameDialog(false);
          showToast({
            type: CPS_NOTIFICATION_TYPE.SUCCESS,
            message: translate('custom.settings.views.dialog.viewRenamed')
          });
        },
        onError: () => {
          showToast({
            type: CPS_NOTIFICATION_TYPE.ERROR,
            message: translate('custom.settings.views.dialog.viewRenamedFailed')
          });
        }
      }
    );
  };

  const deleteViewOnSuccess = (deletedView: CpsGridView): void => {
    const defaultViewId = getDefaultViewIdAfterDelete(views, deletedView.id);

    if (setSelectedView) {
      setSelectedView(defaultViewId);
    }

    showToast({
      type: CPS_NOTIFICATION_TYPE.SUCCESS,
      message: translate('custom.settings.views.viewDeleted')
    });
  };

  const handleDeleteView = (): void => {
    const currentView = findViewById(views, selectedView);

    if (!currentView || !selectedIndex) {
      return;
    }

    const message = translate('custom.settings.views.dialog.deleteConfirmation', {
      viewName: currentView.name
    });

    showDialog({
      title: translate('custom.settings.views.dialog.deleteTitle'),
      message,
      confirmButtonLabel: translate('custom.common.confirm'),
      discardButtonLabel: translate('custom.common.discard'),
      onConfirm: confirmed => {
        if (!confirmed) {
          return undefined;
        }

        return deleteView(
          { viewId: currentView.id, indexId: selectedIndex.id },
          { onSuccess: () => deleteViewOnSuccess(currentView) }
        );
      }
    });
  };

  const showViewCreateFailedToast = (): void => {
    showToast({
      type: CPS_NOTIFICATION_TYPE.ERROR,
      message: translate('custom.settings.views.dialog.viewCreateFailed')
    });
  };

  const handleCreateNewView = (viewName: string): void => {
    if (!currentViewDetails || !selectedIndex) {
      showViewCreateFailedToast();
      return;
    }

    const transformedFields = transformFieldsForCreation(currentViewDetails.fields || []);

    createView(
      {
        indexId: selectedIndex.id,
        name: viewName,
        fields: transformedFields,
        isDefault: false,
        isSystemView: false,
        parentViewId: currentViewDetails.id,
        globalFilters: currentViewDetails.globalFilters.map(filter => ({
          fieldId: filter.id,
          facetsCount: filter.facetsCount
        }))
      },
      {
        onSuccess: newViewId => {
          if (setSelectedView) {
            setSelectedView(newViewId);
          }

          setShowCreateDialog(false);

          showToast({
            type: CPS_NOTIFICATION_TYPE.SUCCESS,
            message: translate('custom.settings.views.dialog.viewCreated')
          });
        },
        onError: showViewCreateFailedToast
      }
    );
  };

  return {
    translate,
    views,
    selectedView,
    isLoading,
    showCreateDialog,
    showRenameDialog,
    DialogComponent,
    buttonStates,
    handleViewChange,
    handleCreatePrivateView: () => setShowCreateDialog(true),
    handleDialogClose: () => setShowCreateDialog(false),
    handleRenameView: () => setShowRenameDialog(true),
    handleRenameDialogClose: () => setShowRenameDialog(false),
    handleUpdateView,
    handleRenameViewSubmit,
    handleDeleteView,
    handleCreateNewView
  };
};
